// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/**
 * Advisor access to the Relationship Cashflow (20-year) projection + its
 * per-profile assumptions. Mirrors the Resource Server's
 * /api/profiles/{profileId}/financial-position/cashflow* surface (proxied by
 * advisor-bff; X-Brokerage-Id injected by the advisorApi interceptor).
 * Values are gross (pre-tax) and in today's money (real terms).
 * See docs/specs/relationship-cashflow-prd.md.
 */

export interface ProjectionAssumptions {
  priceInflationRate: number | null;
  earningsGrowthRate: number | null;
  statePensionAnnual: number | null;
  pensionDrawdownRate: number | null;
  expenditureAtRetirementFactor: number | null;
  defaultSavingsGrowthRate: number | null;
  defaultInvestmentGrowthRate: number | null;
  defaultPropertyGrowthRate: number | null;
  defaultCollectableGrowthRate: number | null;
  childBenefitMonthly: number | null;
}

export interface CashflowYearRow {
  year: number;
  calendarYear: number;
  incomeByCategory: Record<string, number>;
  expenditureByCategory: Record<string, number>;
  /** Gross income. */
  totalIncome: number;
  totalExpenditure: number;
  /** netIncome − totalExpenditure. */
  surplus: number;
  netWorth: number;
  /** Total tax deductions (0 when the tax layer is off). */
  tax: number;
  /** totalIncome − tax. */
  netIncome: number;
  /** Tax split into Income tax / USC / PRSI / DIRT (for the tooltip). */
  taxByComponent: Record<string, number>;
  /** Net worth split into components (Pensions / Investments / Property / Cash &
   * savings / Other assets / Liabilities — last negative); sums to netWorth.
   * Drives the Individual net-worth lines. */
  netWorthByComponent: Record<string, number>;
}

export interface CashflowEvent {
  year: number;
  type: string;
  label: string;
}

export interface RelationshipCashflow {
  horizonYears: number;
  currency: string | null;
  realTerms: boolean;
  gross: boolean;
  /** Effective tax basis of this response: NET (pre-tax) or GROSS (tax netted). */
  basis: 'NET' | 'GROSS';
  /** True when a GROSS forecast was computed NET because the tax layer is off. */
  taxUnavailable: boolean;
  years: CashflowYearRow[];
  events: CashflowEvent[];
  assumptions: ProjectionAssumptions;
  /** Tier-1 assumption keys still unset — the projection is partial until filled in. */
  missingAssumptions: string[];
}

/**
 * A what-if scenario override — a FIGURE (figureRef), a timed ASSUMPTION
 * (assumptionKey), a POLICY_* tweak, or an EVENT that creates / disposes of an
 * item (CASHFLOW_ADD / ACCOUNT_ADD / ASSET_ADD / DISPOSAL). Event overrides
 * carry their detail in `payload`.
 */
export interface ScenarioOverride {
  id: string;
  kind:
    | 'FIGURE'
    | 'ASSUMPTION'
    | 'POLICY_GROWTH'
    | 'POLICY_CONTRIBUTION'
    | 'POLICY_VALUE'
    | 'CASHFLOW_ADD'
    | 'ACCOUNT_ADD'
    | 'ASSET_ADD'
    | 'DISPOSAL'
    | 'SWEEP_SURPLUS'
    | 'SWEEP_RULE';
  figureRef: string | null;
  assumptionKey: string | null;
  validFrom: string;
  amount: number;
  frequency: string | null;
  payload?: Record<string, unknown>;
  /** Who added the override — the workspace marks chat-made vs manual changes. */
  createdBy?: string | null;
}

/** The assumptions a scenario can override (timed), with display labels. */
export const SCENARIO_ASSUMPTION_KEYS: Array<{ key: string; label: string; unit: string }> = [
  { key: 'priceInflationRate', label: 'Inflation rate', unit: '%' },
  { key: 'earningsGrowthRate', label: 'Earnings growth', unit: '%' },
  { key: 'statePensionAnnual', label: 'State pension (annual)', unit: '€' },
  { key: 'expenditureAtRetirementFactor', label: 'Retirement expenditure factor', unit: '×' },
  { key: 'pensionDrawdownRate', label: 'Pension drawdown rate', unit: '%' },
  { key: 'defaultSavingsGrowthRate', label: 'Savings growth (default)', unit: '%' },
  { key: 'defaultInvestmentGrowthRate', label: 'Investment growth (default)', unit: '%' },
  { key: 'defaultPropertyGrowthRate', label: 'Property growth (default)', unit: '%' },
  { key: 'defaultCollectableGrowthRate', label: 'Collectable growth (default)', unit: '%' },
];
export interface Scenario {
  id: string;
  name: string;
  description: string | null;
  status: string;
  /** Per-forecast tax basis (cashflow-modelling-tab PRD): NET (default) or GROSS. */
  basis: 'NET' | 'GROSS';
  /** Create New Forecast modal display anchor; the engine projects from today. */
  displayStartDate: string | null;
  updatedAt: string | null;
  overrides: ScenarioOverride[];
}
/** Net-worth + surplus series per line (base + each scenario) for comparison. */
export interface ScenarioCompare {
  lines: Array<{
    scenarioId: string | null;
    name: string;
    netWorth: Array<{ year: number; value: number }>;
    surplus: Array<{ year: number; value: number }>;
  }>;
}

/** Body for the "Add income/spending" scenario event. */
export interface ScenarioCashflowItemRequest {
  validFrom: string;
  direction: 'INCOME' | 'EXPENDITURE';
  category: string | null;
  label: string;
  amount: number;
  frequency: string;
  ownerClientId: string | null;
  endYear: number | null;
}

/** Body for the "Add account" scenario event. */
export interface ScenarioAccountRequest {
  validFrom: string;
  accountType: string;
  classification: 'ASSET' | 'LIABILITY';
  balance: number;
  interestRate: number | null;
  monthlyRepayment: number | null;
  ownerClientId: string | null;
  label: string | null;
}

/** Body for the "Add asset" scenario event. */
export interface ScenarioAssetRequest {
  validFrom: string;
  assetType: string;
  name: string | null;
  value: number;
  assumedGrowthRate: number | null;
  ownerClientId: string | null;
}

/** Body for the "Sell / dispose" scenario event. targetRef is `asset:{id}` / `investment:{id}` / `holding:{id}`. */
export interface ScenarioDisposalRequest {
  targetRef: string;
  validFrom: string;
  proceeds: number | null;
}

export interface ScenarioSweepSurplusRequest {
  accountRef: string;
  validFrom: string;
}

export interface ScenarioSweepRuleRequest {
  fromRef: string;
  toRef: string;
  thresholdBalance: number;
  mode: 'ALL_OVER' | 'FIXED';
  amount: number | null;
  validFrom: string;
}

export const relationshipCashflowApi = {
  /** The cashflow projection (optionally under a scenario); null when disabled (HTTP 404). */
  getCashflow: async (profileId: string, scenarioId?: string | null): Promise<RelationshipCashflow | null> => {
    try {
      const { data } = await api.get<RelationshipCashflow>(
        `/profiles/${profileId}/financial-position/cashflow`,
        { params: scenarioId ? { scenarioId } : undefined },
      );
      return data;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        return null;
      }
      throw e;
    }
  },

  listScenarios: async (profileId: string): Promise<Scenario[]> => {
    try {
      const { data } = await api.get<Scenario[]>(`/profiles/${profileId}/financial-position/scenarios`);
      return data;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        return [];
      }
      throw e;
    }
  },

  createScenario: async (
    profileId: string,
    body: {
      name: string;
      description: string | null;
      basis?: 'NET' | 'GROSS';
      displayStartDate?: string | null;
    },
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(`/profiles/${profileId}/financial-position/scenarios`, body);
    return data;
  },

  /** Partial update — rename / description / basis / display start (null fields untouched). */
  patchScenario: async (
    profileId: string,
    scenarioId: string,
    body: {
      name?: string;
      description?: string | null;
      basis?: 'NET' | 'GROSS';
      displayStartDate?: string | null;
    },
  ): Promise<Scenario> => {
    const { data } = await api.patch<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}`,
      body,
    );
    return data;
  },

  deleteScenario: async (profileId: string, scenarioId: string): Promise<void> => {
    await api.delete(`/profiles/${profileId}/financial-position/scenarios/${scenarioId}`);
  },

  addScenarioOverride: async (
    profileId: string,
    scenarioId: string,
    body: { figureRef: string; validFrom: string; amount: number; frequency: string | null },
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/overrides`,
      body,
    );
    return data;
  },

  addScenarioAssumptionOverride: async (
    profileId: string,
    scenarioId: string,
    body: { assumptionKey: string; validFrom: string; value: number },
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/assumption-overrides`,
      body,
    );
    return data;
  },

  addScenarioPolicyOverride: async (
    profileId: string,
    scenarioId: string,
    body: { policyRef: string; setting: string; validFrom: string; value: number; mode: string | null },
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/policy-overrides`,
      body,
    );
    return data;
  },

  /** Scenario event — create an income/spending flow as of a date. */
  addScenarioCashflowItem: async (
    profileId: string,
    scenarioId: string,
    body: ScenarioCashflowItemRequest,
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/cashflow-items`,
      body,
    );
    return data;
  },

  /** Scenario event — create an account (asset or liability) as of a date. */
  addScenarioAccount: async (
    profileId: string,
    scenarioId: string,
    body: ScenarioAccountRequest,
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/accounts`,
      body,
    );
    return data;
  },

  /** Scenario event — create an asset as of a date. */
  addScenarioAsset: async (
    profileId: string,
    scenarioId: string,
    body: ScenarioAssetRequest,
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/assets`,
      body,
    );
    return data;
  },

  /** Scenario event — sell / dispose of an asset, investment or holding. */
  addScenarioDisposal: async (
    profileId: string,
    scenarioId: string,
    body: ScenarioDisposalRequest,
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/disposals`,
      body,
    );
    return data;
  },

  addScenarioSweepSurplus: async (
    profileId: string,
    scenarioId: string,
    body: ScenarioSweepSurplusRequest,
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/sweep-surplus`,
      body,
    );
    return data;
  },

  addScenarioSweepRule: async (
    profileId: string,
    scenarioId: string,
    body: ScenarioSweepRuleRequest,
  ): Promise<Scenario> => {
    const { data } = await api.post<Scenario>(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/sweep-rules`,
      body,
    );
    return data;
  },

  removeScenarioOverride: async (profileId: string, scenarioId: string, overrideId: string): Promise<void> => {
    await api.delete(
      `/profiles/${profileId}/financial-position/scenarios/${scenarioId}/overrides/${overrideId}`,
    );
  },

  compareScenarios: async (profileId: string, ids: string[]): Promise<ScenarioCompare> => {
    const { data } = await api.get<ScenarioCompare>(
      `/profiles/${profileId}/financial-position/scenarios/compare`,
      { params: { ids } },
    );
    return data;
  },

  /** The full cashflow report as CSV text (projection + taxes + assumptions + inputs + events). */
  exportCsv: async (profileId: string): Promise<string> => {
    const { data } = await api.get<string>(
      `/profiles/${profileId}/financial-position/cashflow/export`,
      { responseType: 'text' },
    );
    return data;
  },

  getAssumptions: async (profileId: string): Promise<ProjectionAssumptions> => {
    const { data } = await api.get<ProjectionAssumptions>(
      `/profiles/${profileId}/financial-position/cashflow/assumptions`,
    );
    return data;
  },

  /** Set the relationship-level outlook horizon (years) from the tile — merges, doesn't clobber. */
  setHorizon: async (profileId: string, horizonYears: number): Promise<ProjectionAssumptions> => {
    const { data } = await api.put<ProjectionAssumptions>(
      `/profiles/${profileId}/financial-position/cashflow/horizon`,
      { horizonYears },
    );
    return data;
  },

  setAssumptions: async (
    profileId: string,
    body: ProjectionAssumptions,
  ): Promise<ProjectionAssumptions> => {
    const { data } = await api.put<ProjectionAssumptions>(
      `/profiles/${profileId}/financial-position/cashflow/assumptions`,
      body,
    );
    return data;
  },

  /**
   * Download the cashflow CSV report as a first-party browser request — the
   * axios client carries the OAuth cookie + the X-Brokerage-Id header, so no
   * bytes flow through the chat/LLM. {@code scenarioId} null/"base" ⇒ the base
   * cashflow; otherwise that what-if cashflow. Triggers a browser download.
   */
  downloadCashflowCsv: async (profileId: string, scenarioId?: string | null): Promise<void> => {
    const useScenario = scenarioId && scenarioId !== 'base';
    const { data } = await api.get<Blob>(
      `/profiles/${profileId}/financial-position/cashflow/export`,
      {
        params: useScenario ? { scenarioId } : undefined,
        responseType: 'blob',
      },
    );
    const blob = data instanceof Blob ? data : new Blob([String(data)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = `cashflow-${profileId}${useScenario ? `-${scenarioId}` : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(url);
    }
  },

  /**
   * Download the projection as a PDF report (rendered by template-service —
   * summary + assumptions + taxed year table + events + disclaimer). Mirrors
   * the CSV helper; 503 when template-service isn't wired.
   */
  downloadCashflowPdf: async (profileId: string, scenarioId?: string | null): Promise<void> => {
    const useScenario = scenarioId && scenarioId !== 'base';
    const { data } = await api.get<Blob>(
      `/profiles/${profileId}/financial-position/cashflow/export`,
      {
        params: { format: 'pdf', ...(useScenario ? { scenarioId } : {}) },
        responseType: 'blob',
      },
    );
    const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = `cashflow-${profileId}${useScenario ? `-${scenarioId}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};
